import { App, type AppInfo, type RestoredListenerEvent } from "@capacitor/app";
import { compareProductVersions, type MobileClientPolicy } from "@billiard/contracts";
import { Capacitor, type PluginListenerHandle } from "@capacitor/core";
import { type ConnectionStatus, Network } from "@capacitor/network";

export type NativeBarcodeResult =
  | { status: "DETECTED"; value: string }
  | { status: "CANCELLED" }
  | { status: "PERMISSION_DENIED" }
  | { status: "ERROR"; message: string };

let activeBarcodeScan: Promise<NativeBarcodeResult> | null = null;
let restoredBarcodeScan: NativeBarcodeResult | null = null;
let runtimeInitialized = false;
let runtimeInitialization: Promise<boolean> | null = null;
let currentInstalledVersion: string | null = null;
export type NativeClientUpdateState =
  | "POLICY_PENDING"
  | "CURRENT"
  | "UPDATE_AVAILABLE"
  | "UPDATE_REQUIRED"
  | "SERVER_UPDATE_REQUIRED";
let currentUpdateState: NativeClientUpdateState = "POLICY_PENDING";
const updateStateListeners = new Set<(state: NativeClientUpdateState) => void>();

function publishNativeClientUpdateState(state: NativeClientUpdateState): NativeClientUpdateState {
  currentUpdateState = state;
  for (const listener of updateStateListeners) listener(state);
  return state;
}

export function isNativeRuntime(): boolean {
  return Capacitor.isNativePlatform();
}

export async function nativeAppInfo(): Promise<AppInfo | null> {
  if (!isNativeRuntime()) return null;
  const info = await App.getInfo();
  currentInstalledVersion = info.version;
  return info;
}

export async function nativeNetworkStatus(): Promise<ConnectionStatus | null> {
  return isNativeRuntime() ? Network.getStatus() : null;
}

export async function listenForNativeNetwork(
  listener: (status: ConnectionStatus) => void,
): Promise<PluginListenerHandle | null> {
  return isNativeRuntime() ? Network.addListener("networkStatusChange", listener) : null;
}

export async function listenForNativeResume(
  listener: () => void,
): Promise<PluginListenerHandle | null> {
  return isNativeRuntime() ? App.addListener("resume", listener) : null;
}

export function restoredBarcodeResult(event: RestoredListenerEvent): NativeBarcodeResult | null {
  if (event.pluginId !== "CapacitorBarcodeScanner" || event.methodName !== "scanBarcode") {
    return null;
  }
  if (!event.success) return classifyNativeBarcodeError(event.error ?? new Error("扫码恢复失败"));
  const data =
    event.data && typeof event.data === "object" ? (event.data as Record<string, unknown>) : {};
  const value = typeof data.ScanResult === "string" ? data.ScanResult.trim() : "";
  return value ? { status: "DETECTED", value } : { status: "CANCELLED" };
}

export function initializeNativeRuntime(): Promise<boolean> {
  if (!isNativeRuntime() || runtimeInitialized) return Promise.resolve(true);
  if (runtimeInitialization) return runtimeInitialization;
  runtimeInitialization = App.addListener("appRestoredResult", (event) => {
    restoredBarcodeScan = restoredBarcodeResult(event);
  })
    .then(() => {
      runtimeInitialized = true;
      return true;
    })
    .catch(() => false)
    .finally(() => {
      runtimeInitialization = null;
    });
  return runtimeInitialization;
}

export function classifyNativeBarcodeError(error: unknown): NativeBarcodeResult {
  const record = error && typeof error === "object" ? (error as Record<string, unknown>) : {};
  const code = typeof record.code === "string" ? record.code : "";
  const message = error instanceof Error ? error.message : "原生扫码启动失败";
  if (code.endsWith("0006") || /cancel/i.test(message)) return { status: "CANCELLED" };
  if (code.endsWith("0007") || /permission|access.*denied/i.test(message)) {
    return { status: "PERMISSION_DENIED" };
  }
  return { status: "ERROR", message };
}

export function nativeUpdateAvailable(installedVersion: string, serviceVersion: string): boolean {
  try {
    return compareProductVersions(installedVersion, serviceVersion) < 0;
  } catch {
    return false;
  }
}

export function nativeClientUpdateState(
  installedVersion: string,
  policy: MobileClientPolicy,
): NativeClientUpdateState {
  try {
    if (compareProductVersions(installedVersion, policy.minimumSupportedVersion) < 0) {
      return "UPDATE_REQUIRED";
    }
    if (compareProductVersions(installedVersion, policy.latestVersion) > 0) {
      return "SERVER_UPDATE_REQUIRED";
    }
    return nativeUpdateAvailable(installedVersion, policy.latestVersion)
      ? "UPDATE_AVAILABLE"
      : "CURRENT";
  } catch {
    return "UPDATE_REQUIRED";
  }
}

export function setNativeClientUpdatePolicy(
  installedVersion: string,
  policy: MobileClientPolicy,
): NativeClientUpdateState {
  return publishNativeClientUpdateState(nativeClientUpdateState(installedVersion, policy));
}

export function clearNativeClientUpdatePolicy(): NativeClientUpdateState {
  return publishNativeClientUpdateState("POLICY_PENDING");
}

export function nativeClientVersionHeader(): string | null {
  return currentInstalledVersion;
}

export function markNativeClientUpdateRequired(
  state: "UPDATE_REQUIRED" | "SERVER_UPDATE_REQUIRED",
): NativeClientUpdateState {
  return publishNativeClientUpdateState(state);
}

export function subscribeNativeClientUpdateState(
  listener: (state: NativeClientUpdateState) => void,
): () => void {
  listener(currentUpdateState);
  updateStateListeners.add(listener);
  return () => updateStateListeners.delete(listener);
}

export function nativeClientWritesBlocked(): boolean {
  return currentUpdateState !== "CURRENT" && currentUpdateState !== "UPDATE_AVAILABLE";
}

export function scanNativeProductBarcode(): Promise<NativeBarcodeResult> {
  if (!isNativeRuntime()) return Promise.resolve({ status: "ERROR", message: "当前不是原生 App" });
  if (restoredBarcodeScan) {
    const restored = restoredBarcodeScan;
    restoredBarcodeScan = null;
    return Promise.resolve(restored);
  }
  if (activeBarcodeScan) return activeBarcodeScan;
  activeBarcodeScan = import("@capacitor/barcode-scanner")
    .then(
      ({
        CapacitorBarcodeScanner,
        CapacitorBarcodeScannerCameraDirection,
        CapacitorBarcodeScannerScanOrientation,
        CapacitorBarcodeScannerTypeHint,
      }) =>
        CapacitorBarcodeScanner.scanBarcode({
          hint: CapacitorBarcodeScannerTypeHint.ALL,
          scanInstructions: "把商品条码放在取景框内",
          scanButton: false,
          scanText: "确认条码",
          cameraDirection: CapacitorBarcodeScannerCameraDirection.BACK,
          scanOrientation: CapacitorBarcodeScannerScanOrientation.ADAPTIVE,
          cancelButtonAccessibilityLabel: "取消扫码",
          torchButtonOnAccessibilityLabel: "关闭补光灯",
          torchButtonOffAccessibilityLabel: "打开补光灯",
        }),
    )
    .then((result): NativeBarcodeResult => {
      const value = result.ScanResult.trim();
      return value ? { status: "DETECTED", value } : { status: "CANCELLED" };
    })
    .catch(classifyNativeBarcodeError)
    .finally(() => {
      activeBarcodeScan = null;
    });
  return activeBarcodeScan;
}
